// Attach one document to one car for one month.
//
// Luyanda: "the registration documents do not have an upload for it, please add
// it."
//
// WHY THIS IS NOT THE SCHEDULE DIALOG. That one exists for a supplier schedule:
// one document listing many cars, read by a machine, confirmed line by line by a
// person. Registration has no such document. Xero holds every licensing receipt
// with an attachment on it, and not one carries a registration number in any
// field, so no reader will ever match them to a car and one that guessed would
// be worse than none.
//
// So this dialog asks the only question a machine cannot answer: which car is
// this receipt for. The person answers it, and the extract records that they
// answered it and who they are. That is weaker evidence than a supplier schedule
// and the screen says so rather than dressing it up.
import { useCallback, useRef, useState } from 'react'
import { Paperclip, CircleCheck, CircleAlert } from 'lucide-react'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Spinner } from '../../lib/shadcn/spinner'
import { useUploadSupportingDocument } from '../../hooks/backend/statements'
import { formatZar } from '../../utils/statements'
import { cn } from '../../lib/shadcn/utils'
import { lineLabel, monthLabel } from '../../utils/evidenceCoverage'

function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('That file could not be read from this device.'))
    reader.onload = () => {
      const raw = String(reader.result ?? '')
      const comma = raw.indexOf(',')
      resolve(comma >= 0 ? raw.slice(comma + 1) : raw)
    }
    reader.readAsDataURL(file)
  })
}

type SoleInvoice = {
  plate: string
  line_label: string
  charged_zar: number
  document_zar: number
  delta_zar: number
  handled: string
  settles_in: string | null
  note: string
}

type Result = {
  document_id: string | null
  extract_id: string | null
  closes_the_gap: boolean
  filename: string | null
  sole_invoice: SoleInvoice | null
}

export function AttachDocumentDialog({
  open,
  onOpenChange,
  vehicleId,
  plate,
  lineCode,
  month,
  chargedZar,
  onAttached,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  vehicleId: string
  plate: string
  lineCode: string
  month: string
  chargedZar: number
  onAttached: () => void
}) {
  const upload = useUploadSupportingDocument<Result>()
  const fileRef = useRef<HTMLInputElement | null>(null)
  const [file, setFile] = useState<File | null>(null)
  const [supplier, setSupplier] = useState('')
  const [documentNumber, setDocumentNumber] = useState('')
  const [amount, setAmount] = useState('')
  const [note, setNote] = useState('')
  const [soleInvoice, setSoleInvoice] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<Result | null>(null)

  const reset = useCallback(() => {
    setFile(null); setSupplier(''); setDocumentNumber(''); setAmount('')
    setNote(''); setError(null); setDone(null); setBusy(false)
    if (fileRef.current) fileRef.current.value = ''
  }, [])

  const close = useCallback((v: boolean) => {
    if (!v) reset()
    onOpenChange(v)
  }, [onOpenChange, reset])

  const submit = useCallback(async () => {
    if (!file) { setError('Choose the document first.'); return }
    setBusy(true); setError(null)
    try {
      const b64 = await toBase64(file)
      const res = await upload.trigger({
        vehicle_id: vehicleId,
        period_start: `${month}-01`,
        line_code: lineCode,
        filename: file.name,
        content_type: file.type || 'application/pdf',
        content_base64: b64,
        amount_zar: amount.trim() === '' ? null : Number(amount),
        supplier: supplier.trim() === '' ? null : supplier.trim(),
        document_number: documentNumber.trim() === '' ? null : documentNumber.trim(),
        note: note.trim() === '' ? null : note.trim(),
        sole_invoice: soleInvoice,
      })
      setDone(res as Result)
      onAttached()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }, [amount, documentNumber, file, lineCode, month, note, onAttached, soleInvoice, supplier, upload, vehicleId])

  const typed = amount.trim() === '' ? null : Number(amount)
  const variance = typed !== null && Number.isFinite(typed) ? chargedZar - typed : null

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Paperclip className="h-4 w-4" />
            Attach a document to {plate}
          </DialogTitle>
          <DialogDescription>
            {lineLabel(lineCode)}, {monthLabel(month)}. The owner was charged{' '}
            {formatZar(chargedZar)}.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="space-y-3">
            <div className="flex items-start gap-2 rounded-md bg-emerald-50 p-3 text-sm text-emerald-900">
              <CircleCheck className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <p className="font-medium">{done.filename} is attached to {plate}.</p>
                <p className="mt-1 text-xs">
                  {done.closes_the_gap
                    ? 'This month now has a document behind it for this car.'
                    : 'This car and month already had evidence from a supplier schedule, which is stronger than a document filed by hand, so that has been left in place. Your document is stored and linked beside it.'}
                </p>
              </div>
            </div>

            {done.sole_invoice && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                <p className="font-medium">
                  {done.sole_invoice.handled === 'nothing to do'
                    ? 'No change was needed'
                    : done.sole_invoice.handled === 'changed in the draft'
                      ? 'The draft was changed'
                      : 'Credited forward'}
                </p>
                <p className="mt-1 text-xs">{done.sole_invoice.note}</p>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => reset()}>Attach another</Button>
              <Button onClick={() => close(false)}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="rounded-md bg-muted p-3 text-xs leading-relaxed text-muted-foreground">
              No reader can match this document to a car, because the receipts for this charge
              carry no registration number. You are the one saying it belongs to {plate}, and the
              record will say that you said it.
            </p>

            <div>
              <label className="mb-1 block text-xs font-medium text-foreground">Document</label>
              <input
                ref={fileRef}
                type="file"
                accept="application/pdf,image/*"
                className="block w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border file:border-border file:bg-card file:px-3 file:py-1.5 file:text-sm file:text-foreground"
                onChange={(e) => { setFile(e.target.files?.[0] ?? null); setError(null) }}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-foreground">Supplier</label>
                <Input value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="JMPD" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-foreground">Reference</label>
                <Input value={documentNumber} onChange={(e) => setDocumentNumber(e.target.value)} placeholder="RB20986458130" />
              </div>
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-foreground">
                Amount on the document, including VAT
              </label>
              <Input
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Leave blank if you are not capturing it"
              />
              {variance !== null && Number.isFinite(variance) && Math.abs(variance) >= 0.01 && (
                <p className="mt-1 text-xs text-amber-700">
                  The owner was charged {formatZar(chargedZar)} and this document says{' '}
                  {formatZar(typed as number)}, a difference of {formatZar(Math.abs(variance))}.
                  Attach it anyway if that is right; the difference is recorded either way.
                </p>
              )}
            </div>

            {/* "Should this be treated as the only invoice for this item for
                this month?" Luyanda, 9 September 2026. The scope is this car:
                the line becomes the invoice and the rest of it clears. It is
                deliberately not a fleet-wide switch, and it needs an amount,
                because clearing a balance to a figure nobody typed is how an
                owner is credited by accident. */}
            <label
              className={cn(
                'flex cursor-pointer items-start gap-2 rounded-md border p-3 text-xs',
                soleInvoice ? 'border-amber-300 bg-amber-50' : 'border-border',
              )}
            >
              <input
                type="checkbox"
                className="mt-0.5 h-3.5 w-3.5"
                checked={soleInvoice}
                onChange={(e) => setSoleInvoice(e.target.checked)}
                disabled={typed === null || !Number.isFinite(typed)}
              />
              <span>
                <span className="font-medium text-foreground">
                  Treat this as the only invoice for {lineLabel(lineCode)} on {plate} in{' '}
                  {monthLabel(month)}
                </span>
                <span className="mt-0.5 block text-muted-foreground">
                  {typed === null || !Number.isFinite(typed)
                    ? 'Capture the amount on the document first. Clearing a balance to a figure nobody typed is how an owner is credited by accident.'
                    : variance !== null && variance > 0.005
                      ? `${lineLabel(lineCode)} becomes ${formatZar(typed)} and ${formatZar(variance)} comes off. ` +
                        'No other vehicle is touched. If the month has already been issued it is not restated: the owner is credited on the next draft statement.'
                      : 'The document already accounts for the whole charge, so nothing would change.'}
                </span>
              </span>
            </label>

            <div>
              <label className="mb-1 block text-xs font-medium text-foreground">Note</label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything the next person needs to know" />
            </div>

            {error && (
              <div className="flex items-start gap-2 rounded-md bg-red-50 p-3 text-sm text-red-800">
                <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => close(false)} disabled={busy}>Cancel</Button>
              <Button onClick={submit} disabled={busy || !file}>
                {busy ? <><Spinner className="mr-2 h-4 w-4" /> Attaching</> : 'Attach to ' + plate}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
